import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getArticlesByCategory, articles } from '@/mocks/articles';

const baseUrl = 'https://duxiaoguan.cn';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">分类不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryArticles = getArticlesByCategory(category.slug);
  const hotArticles = articles.filter((a) => a.isHot && a.category === category.slug);

  const schemaJson = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${baseUrl}/#website`,
        url: baseUrl,
        name: '吃瓜网在线',
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'CollectionPage',
        '@id': `${baseUrl}/category/${category.slug}/#collectionpage`,
        url: `${baseUrl}/category/${category.slug}`,
        name: `${category.name} - 吃瓜网在线`,
        description: `${category.description}，每日更新最新${category.name}资讯与深度爆料。`,
        isPartOf: { '@id': `${baseUrl}/#website` },
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: category.name },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 吃瓜网在线 | 最新${category.name}资讯`}
        description={`${category.description}，每日更新最新${category.name}资讯与深度爆料。吃瓜网在线为您提供第一手${category.name}内容。`}
        keywords={`${category.name},吃瓜网在线,在线吃瓜,娱乐八卦,51吃瓜,黑料吃瓜`}
        canonicalUrl={`${baseUrl}/category/${category.slug}`}
        schemaJson={schemaJson}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              <div className="w-full lg:w-[40%] flex items-center px-4 md:px-6 py-16 lg:py-24">
                <div>
                  <span className="inline-block text-xs bg-primary-100/80 text-primary-600 px-3 py-1 rounded-full font-label mb-3">
                    {category.name}
                  </span>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-3">
                    {category.name}
                  </h1>
                  <p className="text-base text-foreground-600 leading-relaxed">
                    {category.description}
                  </p>
                </div>
              </div>
              <div className="w-full lg:w-[60%] h-[240px] lg:h-[400px] overflow-hidden bg-background-100">
                <img
                  src={category.imageUrl}
                  alt={category.name}
                  title={category.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />

              <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
                <div>
                  {hotArticles.length > 0 && (
                    <div className="mb-10">
                      <div className="flex items-center gap-2 mb-5">
                        <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                        <h2 className="text-lg font-heading font-bold text-foreground-900">
                          热门{category.name}
                        </h2>
                      </div>
                      <div className="overflow-x-auto pb-2">
                        <div className="flex gap-4 min-w-max">
                          {hotArticles.map((article) => (
                            <div key={article.slug} className="w-[260px] shrink-0">
                              <ArticleCard
                                slug={article.slug}
                                title={article.title}
                                excerpt={article.excerpt}
                                categoryName={article.categoryName}
                                categorySlug={article.category}
                                imageUrl={article.imageUrl}
                                publishDate={article.publishDate}
                                readCount={article.readCount}
                                isHot={article.isHot}
                                isNew={article.isNew}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center gap-2 mb-5">
                      <span className="w-1.5 h-5 rounded-full bg-secondary-500"></span>
                      <h2 className="text-lg font-heading font-bold text-foreground-900">
                        全部{category.name}
                      </h2>
                    </div>
                    <div className="flex flex-col gap-4">
                      {categoryArticles.map((article) => (
                        <ArticleCard
                          key={article.slug}
                          slug={article.slug}
                          title={article.title}
                          excerpt={article.excerpt}
                          categoryName={article.categoryName}
                          categorySlug={article.category}
                          imageUrl={article.imageUrl}
                          publishDate={article.publishDate}
                          readCount={article.readCount}
                          isHot={article.isHot}
                          isNew={article.isNew}
                          variant="horizontal"
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <aside className="hidden lg:block">
                  <div className="sticky top-20">
                    <div className="bg-background-50 rounded-lg p-5 border border-background-200/70 mb-5">
                      <h3 className="text-base font-heading font-bold text-foreground-900 mb-4">
                        其他分类
                      </h3>
                      <div className="flex flex-col gap-2">
                        {categories
                          .filter((c) => c.slug !== category.slug)
                          .map((c) => (
                            <Link
                              key={c.slug}
                              to={`/category/${c.slug}`}
                              className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer py-1.5 whitespace-nowrap"
                            >
                              <i className="ri-arrow-right-s-line text-foreground-400"></i>
                              {c.name}
                            </Link>
                          ))}
                      </div>
                    </div>

                    <div className="bg-primary-50 rounded-lg p-5">
                      <h3 className="text-base font-heading font-bold text-primary-700 mb-3">
                        有猛料？
                      </h3>
                      <p className="text-sm text-foreground-600 mb-3 leading-relaxed">
                        我们严格保护您的隐私，匿名投稿安全可靠。
                      </p>
                      <Link
                        to="/about"
                        className="inline-block w-full text-center whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label py-2.5 rounded-lg transition-colors cursor-pointer"
                      >
                        立即爆料
                      </Link>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}